<template>
    <div v-if="routine" class="routine-preview">
        <div v-if="!hideName || displayDate" class="preview-header">
            <div v-if="!hideName" class="preview-name text-truncate">{{ displayName }}</div>
            <div v-if="displayDate" class="preview-date">{{ displayDate }}</div>
        </div>

        <div class="preview-stats">
            <div class="preview-stat">
                <div class="preview-stat-lbl">{{ $tc('common.minutes', 2) }}</div>
                <div class="preview-stat-val">{{ routineDuration }}</div>
            </div>
            <div class="preview-stat">
                <div class="preview-stat-lbl">{{ $tc('common.blocks', 2) }}</div>
                <div class="preview-stat-val">{{ blocksCount }}</div>
            </div>
            <div class="preview-stat">
                <div class="preview-stat-lbl">{{ $tc('exercises.exercises', 2) }}</div>
                <div class="preview-stat-val">{{ exercisesCount }}</div>
            </div>
        </div>

        <div class="preview-blocks">
            <div v-for="(block, bi) in blocks" :key="bi" class="pv-block" :class="blockClass(bi)">
                <button type="button" class="pv-block-header" @click="toggle(bi)">
                    <span class="pv-block-name">{{ blockLabel(block, bi) }}</span>
                    <span class="pv-block-count">{{ blockExercises(block).length }}</span>
                    <v-icon class="pv-chevron" :class="{ open: isOpen(bi) }" size="18" color="txtMuted">
                        mdi-chevron-down
                    </v-icon>
                </button>
                <div v-show="isOpen(bi)" class="pv-exercises">
                    <div v-for="(ex, ei) in blockExercises(block)" :key="ei" class="pv-ex">
                        <span class="pv-ex-num">{{ ei + 1 }}</span>
                        <span class="pv-ex-name text-truncate">{{ ex.name }}</span>
                        <span class="pv-ex-detail">{{ exerciseDetail(ex, block) }}</span>
                    </div>
                </div>
            </div>
        </div>

        <div class="preview-cta">
            <v-btn block large depressed color="primary" class="black--text start-btn" @click="$emit('start', routine)">
                {{ startLabel }}
                <v-icon right size="20">mdi-arrow-right</v-icon>
            </v-btn>
        </div>
    </div>
</template>

<script>
import { blockUniqueTime } from "@/utils/Exercises";

export default {
    name: "RoutinePreview",
    props: {
        routine: {
            type: Object,
            default: null,
        },
        nameLabel: {
            type: String,
            default: null,
        },
        dateText: {
            type: String,
            default: null,
        },
        hideName: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            closed: {},
        }
    },
    computed: {
        routineName(vm = this) {
            return vm.routine?.name || vm.routine?.versus || ''
        },
        dateLabel(vm = this) {
            const date = vm.routine?.rawDate?.full
            if (!date) return ''
            const label = vm.$moment(date).format(vm.$t('common.date_format_day_month'))
            return label.charAt(0).toUpperCase() + label.slice(1)
        },
        displayName(vm = this) {
            return vm.nameLabel != null ? vm.nameLabel : vm.routineName
        },
        displayDate(vm = this) {
            return vm.dateText != null ? vm.dateText : vm.dateLabel
        },
        blocks(vm = this) {
            return blockUniqueTime(vm.routine?.blocks || [])
        },
        blocksCount(vm = this) {
            return vm.routine?.blocks?.length || 0
        },
        exercisesCount(vm = this) {
            let exercises = 0
            vm.routine?.blocks?.forEach(block => {
                exercises += block?.exercises?.filter(exercise => exercise?.type === 'exercise')?.length || 0
            })
            return exercises
        },
        routineDuration(vm = this) {
            if (!vm.routine) return vm.$t('common.not_available_short')
            let minutes = 0
            vm.routine.blocks.forEach(block => {
                block.exercises.forEach(exercise => {
                    minutes += exercise?.config?.sets?.length || block.sets || 0
                })
            })
            const minutesAdded = Number(minutes * 1.3).toFixed()
            if (Number(minutes) === Number(minutesAdded)) return minutes
            return `${minutes} - ${minutesAdded}`
        },
        hasPre(vm = this) {
            return !!(vm.routine?.advance?.pre)
        },
        hasPost(vm = this) {
            return !!(vm.routine?.advance?.post)
        },
        startLabel(vm = this) {
            if (vm.hasPre && vm.hasPost) return vm.$t('common.see_again')
            if (vm.hasPre) return vm.$t('common.continue')
            return vm.$t('routines.start_session')
        },
    },
    methods: {
        isOpen(index) {
            return !this.closed[index]
        },
        toggle(index) {
            this.$set(this.closed, index, !this.closed[index])
        },
        blockClass(index) {
            return `block-${['a', 'b', 'c', 'd'][index % 4]}`
        },
        blockLabel(block, index) {
            const vm = this
            return vm.blockNameLabel(block.name) || `${vm.$tc('common.blocks', 1)} ${index + 1}`
        },
        blockExercises(block) {
            return (block.exercises || []).filter(ex => ex.type !== 'rest')
        },
        exerciseDetail(exercise, block) {
            const vm = this
            if (block && block.isCircuit) {
                if (block.type === 'tabata') return vm.$t('circuits.tabata')
                const value = exercise?.config?.value
                const type = vm.$t(`exercises.short_${exercise?.config?.type || 'reps'}`)
                return `${block.sets || 1} × ${value != null ? value : ''} ${type}`.trim()
            }
            const sets = exercise?.config?.sets || []
            if (!sets.length) return ''
            const data = sets[0]?.data || []
            const primary = data.find(d => d?.short_name && d.short_name !== 'short_weight') || data[0]
            const typeKey = (primary?.short_name || 'short_reps').replace('short_', '')
            const typeLabel = vm.$te(`exercises.${typeKey}`) ? vm.$t(`exercises.${typeKey}`).toLowerCase() : ''
            let detail = `${sets.length}×${primary?.value != null ? primary.value : ''} ${typeLabel}`.trim()
            const weight = data.find(d => d?.short_name === 'short_weight')
            if (weight && Number(weight.value) > 0) detail += ` · ${weight.value}${vm.$t('exercises.short_weight')}`
            return detail
        },
    },
}
</script>

<style scoped>
.routine-preview {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.preview-header {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.preview-name {
    font-size: 18px;
    font-weight: 700;
    color: var(--v-txtPrimary-base);
}

.preview-date {
    font-size: 13px;
    font-weight: 500;
    color: var(--v-txtSecondary-base);
}

.preview-stats {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
}

.preview-stat {
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 12px;
    padding: 12px 8px;
    text-align: center;
}

.preview-stat-lbl {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--v-txtMuted-base);
    margin-bottom: 4px;
}

.preview-stat-val {
    font-size: 20px;
    font-weight: 800;
    color: var(--v-txtPrimary-base);
}

.preview-blocks {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.pv-block {
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 12px;
    overflow: hidden;
}

.pv-block-header {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 14px;
    background: transparent;
    border: none;
    cursor: pointer;
    font-family: inherit;
    text-align: left;
}

.pv-block-name {
    flex: 1;
    min-width: 0;
    font-size: 13px;
    font-weight: 700;
    color: var(--v-txtPrimary-base);
}

.block-a .pv-block-name {
    color: var(--v-accent-base);
}

.block-b .pv-block-name {
    color: var(--v-success-base);
}

.block-c .pv-block-name {
    color: #7C5CBF;
}

.block-d .pv-block-name {
    color: #D4782A;
}

.pv-block-count {
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    border-radius: 10px;
    background: var(--v-surface2-base);
    color: var(--v-txtSecondary-base);
    font-size: 11px;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.pv-chevron {
    transition: transform 0.2s;
}

.pv-chevron.open {
    transform: rotate(180deg);
}

.pv-exercises {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 0 10px 10px;
}

.pv-ex {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    background: var(--v-surface2-base);
    border-radius: 8px;
    min-width: 0;
}

.pv-ex-num {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 9px;
    font-weight: 800;
    color: #fff;
    flex-shrink: 0;
}

.block-a .pv-ex-num {
    background: var(--v-accent-base);
}

.block-b .pv-ex-num {
    background: var(--v-success-base);
}

.block-c .pv-ex-num {
    background: #7C5CBF;
}

.block-d .pv-ex-num {
    background: #D4782A;
}

.pv-ex-name {
    flex: 1;
    min-width: 0;
    font-size: 13px;
    font-weight: 600;
    color: var(--v-txtPrimary-base);
}

.pv-ex-detail {
    font-size: 11px;
    color: var(--v-txtMuted-base);
    flex-shrink: 0;
    white-space: nowrap;
}

.preview-cta .start-btn {
    border-radius: 12px;
    font-weight: 700;
    text-transform: none;
    letter-spacing: 0;
}
</style>
